/**
 * Sign in.
 *
 * THREE FAILURES ARE THREE DIFFERENT SENTENCES, and that is most of this file.
 * `bad_credentials`, `account_disabled` and `rate_limited` mean "try again
 * carefully", "stop trying and ask an administrator" and "stop trying for eleven
 * minutes" — three different next actions. Rendering the server's message for all
 * three would be shorter and would leave a person retyping a password that was
 * never the problem. Everything else falls through to the server's own message,
 * because a code this screen has not thought about is better reported verbatim
 * than translated into a guess.
 *
 * `bad_credentials` ALSO MEANS "INVITED, NEVER REGISTERED" — the contract says so
 * explicitly, and the panel cannot distinguish the two without telling an
 * attacker which addresses exist. So the message names both possibilities. It is
 * one extra clause and it is the difference between somebody finding the invite
 * email in their spam folder and somebody filing a ticket.
 *
 * THE WAIT IS IN WORDS, FROM `Retry-After`. "429" is not a wait and "please try
 * again later" is not one either. The header is the only place the number exists,
 * which is why ApiError carries it.
 *
 * NO SUCCESS MESSAGE. `refresh()` replaces this whole screen with the panel; a
 * "signed in" line would be read by nobody, on a screen that no longer exists.
 */
import { useState } from 'react'
import { ApiError } from '@/api'
import { auth } from '@/lib/auth'
import { useAuth } from '@/components/auth-provider'
import { AuthShell } from '@/components/auth-shell'
import { Link } from '@/components/app-sidebar'
import { PasswordField } from '@/components/password-field'
import { Button } from '@/components/ui/button'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'

/**
 * A wait a person can act on. Minutes once it is over ninety seconds, because
 * "in 754 seconds" is a number somebody has to do arithmetic on to use.
 */
function inWords(seconds: number): string {
  if (seconds <= 1) return 'in a moment'
  if (seconds < 90) return `in ${seconds} seconds`
  const minutes = Math.ceil(seconds / 60)
  return `in about ${minutes} minute${minutes === 1 ? '' : 's'}`
}

function messageFor(e: unknown): string {
  if (!(e instanceof ApiError)) {
    return e instanceof Error ? e.message : String(e)
  }
  switch (e.code) {
    case 'bad_credentials':
      return 'That email and password do not match an account. If you were invited but have never signed in, use the link in your invitation email instead — a password has not been set yet.'
    case 'account_disabled':
      return 'This account has been disabled. An administrator has to re-enable it; the password is not the problem.'
    case 'rate_limited':
      return `Too many sign-in attempts. Try again ${
        e.retryAfterSeconds === undefined ? 'in a few minutes' : inWords(e.retryAfterSeconds)
      }.`
    default:
      return e.message
  }
}

export function LoginPage({
  /**
   * The path this person actually asked for, when they were sent here by the
   * gate rather than arriving at /login.
   *
   * It is a LABEL AND NOT A REDIRECT. App.tsx renders this screen in place
   * without touching the URL, so a successful sign-in re-renders the requested
   * screen at the address that is already in the bar — there is nothing to
   * navigate back to. Showing it matters anyway: somebody who followed a link to
   * one session and got a login form needs to know the link was not broken.
   */
  wanted,
}: {
  wanted?: string
}) {
  const { refresh, error: sessionError } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    // A write, so a timeout signal and not a controller tied to this component:
    // the request must survive the unmount that its own success causes. See the
    // note on Save in AgentEdit.tsx.
    auth
      .login(email, password, AbortSignal.timeout(20_000))
      .then(() => {
        // The response body is not proof the cookie stuck. refresh() asks the
        // server who we are, and that answer is the one the app runs on.
        refresh()
      })
      .catch((err: unknown) => {
        setBusy(false)
        setError(messageFor(err))
      })
  }

  return (
    <AuthShell
      title="Welcome back"
      intro={
        wanted && wanted !== '/'
          ? <>You asked for <span className="mono">{wanted}</span>. Sign in and it will open.</>
          : 'Voice AI Agents Control Panel'
      }
    >
      <form onSubmit={onSubmit}>
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="email">Email</FieldLabel>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              autoFocus
            />
          </Field>

          {/* NO RULE HINT HERE. This password already exists; telling somebody
              recalling it that it needs twelve characters is advice about a
              decision made months ago, and reads as a complaint about their
              typing. The forgot link sits on the label row (login-03's
              placement): it belongs to this field, not to the page. */}
          <PasswordField
            id="password"
            label="Password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
            labelEnd={
              <Link to="/forgot-password" className="underline-offset-4 hover:underline">
                Forgot your password?
              </Link>
            }
          />

          {error && <p className="err text-sm">{error}</p>}

          {/* The session check itself failed — the panel is unreachable rather
              than refusing us. Worth saying before somebody blames their
              password for a network fault. */}
          {!error && sessionError && (
            <p className="text-xs text-muted-foreground">
              Note: checking your existing session failed ({sessionError}). Signing in may
              fail for the same reason.
            </p>
          )}

          <Field>
            <Button type="submit" disabled={busy || email === '' || password === ''}>
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
            {/* Accounts are invite-only — there is no sign-up to link to, so the
                login-03 "Sign up" slot points at a person instead. */}
            <FieldDescription className="text-center">
              Don&apos;t have an account?{' '}
              <a href="mailto:support@4js.com" className="underline underline-offset-4">
                Contact support
              </a>
              .
            </FieldDescription>
          </Field>
        </FieldGroup>
      </form>
    </AuthShell>
  )
}
